class Core7Carousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.prevButtons = Array.from(this.querySelectorAll('[data-carousel-prev]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-carousel-next]'));
    this.dotsContainer = this.querySelector('[data-carousel-dots]');
    // Optional numeric "n / N" indicator (used on mobile where the dots are
    // hidden). Absent in sections that only use dots — guarded everywhere.
    this.counter = this.querySelector('[data-carousel-counter]');
    // The controls wrapper holds both the arrows and the dots in our sections.
    this.controls = this.dotsContainer ? this.dotsContainer.parentElement : null;
    // Optional desktop side arrows (Experts + Testimonials). Separate from the
    // footer/control bar so they can stay visible when footer arrows hide.
    this.sideArrows = this.querySelector('[data-carousel-side-arrows]');

    if (!this.track || this.slides.length === 0) return;

    this.page = 0;

    this.prevButtons.forEach((button) => {
      button.addEventListener('click', () => this.goToPage(this.page - 1));
    });
    this.nextButtons.forEach((button) => {
      button.addEventListener('click', () => this.goToPage(this.page + 1));
    });

    // Keep the active page in sync while the user scrolls/swipes manually.
    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // The number of visible cards (and so the page count) changes with width.
    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);

    this.measure();
    this.buildDots();
    this.update();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.settleTimer);
  }

  // Work out how many whole cards fit per page and how many pages that makes.
  measure() {
    const first = this.slides[0];
    const stride =
      this.slides.length > 1 ? this.slides[1].offsetLeft - first.offsetLeft : first.offsetWidth;
    this.cardStride = stride > 0 ? stride : this.track.clientWidth || 1;

    // Floor so only fully visible cards count toward a page. A partial peek of
    // the next card is left for the following page — every card on the current
    // page stays completely in view (no clipped edge cards).
    this.perPage = Math.max(1, Math.floor((this.track.clientWidth + 1) / this.cardStride));
    this.pageCount = Math.max(1, Math.ceil(this.slides.length / this.perPage));
    this.page = Math.min(this.page, this.pageCount - 1);
  }

  buildDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.pageCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'core7-carousel__dot';
      dot.setAttribute('aria-label', `Go to page ${i + 1} of ${this.pageCount}`);
      dot.addEventListener('click', () => this.goToPage(i));
      this.dotsContainer.appendChild(dot);
    }

    this.dots = Array.from(this.dotsContainer.querySelectorAll('.core7-carousel__dot'));
  }

  goToPage(nextPage) {
    const page = Math.max(0, Math.min(this.pageCount - 1, nextPage));
    this.page = page;

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const isLastPage = page >= this.pageCount - 1;

    // Last page always pins to the end so the final card is fully visible.
    // Intermediate pages align to the first card of that page.
    let target;
    if (isLastPage) {
      target = maxScroll;
    } else {
      const firstSlide = this.slides[page * this.perPage];
      target = firstSlide ? firstSlide.offsetLeft : page * this.perPage * this.cardStride;
      target = Math.min(Math.max(0, target), maxScroll);
    }

    // Drive the controls from the destination page and ignore the intermediate
    // scroll positions of the smooth-scroll animation. Otherwise the arrows
    // briefly re-toggle their disabled state mid-flight (a visible flash).
    if (Math.abs(target - this.track.scrollLeft) > 2) {
      this.targetLeft = target;
      this.isAnimating = true;
      clearTimeout(this.settleTimer);

      // Disable snap while we programmatically land on the last page; otherwise
      // snap-align:start can pull us short and clip the final card.
      if (isLastPage) {
        this.track.style.scrollSnapType = 'none';
      }

      this.settleTimer = setTimeout(() => {
        this.isAnimating = false;
        if (isLastPage) {
          const end = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
          if (Math.abs(this.track.scrollLeft - end) > 1) {
            this.track.scrollTo({ left: end, behavior: 'auto' });
          }
          this.page = this.pageCount - 1;
          this.track.style.scrollSnapType = '';
        }
        this.syncControls();
      }, 700);
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    } else if (isLastPage) {
      // Already near the end — still pin exactly so the last card isn't clipped.
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({ left: maxScroll, behavior: 'auto' });
      this.track.style.scrollSnapType = '';
      this.page = this.pageCount - 1;
    }

    this.syncControls();
  }

  // True when the track is close enough to the end that the last card is
  // effectively fully in view. Scroll-snap often stops 1–20px short of
  // maxScroll, so a tiny epsilon alone is not enough.
  isAtScrollEnd() {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    if (maxScroll <= 0) return true;
    const remaining = maxScroll - this.track.scrollLeft;
    return remaining <= Math.max(4, this.cardStride * 0.2);
  }

  // Derive the active page from the current scroll position.
  currentPageFromScroll() {
    if (this.isAtScrollEnd()) return this.pageCount - 1;

    const pageStride = this.perPage * this.cardStride;
    if (pageStride <= 0) return 0;
    return Math.max(0, Math.min(this.pageCount - 1, Math.round(this.track.scrollLeft / pageStride)));
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;

      // While a click-driven smooth scroll is running, keep the controls on the
      // destination page; only resume tracking once the scroll has settled.
      if (this.isAnimating) {
        if (Math.abs(this.track.scrollLeft - this.targetLeft) <= 2) {
          this.isAnimating = false;
        }
        return;
      }

      this.page = this.currentPageFromScroll();
      this.syncControls();
    });
  }

  onResize() {
    if (this.resizeRaf) return;
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      const previousPageCount = this.pageCount;
      this.measure();
      if (this.pageCount !== previousPageCount) this.buildDots();
      this.page = this.currentPageFromScroll();
      this.update();
    });
  }

  update() {
    // Hide navigation when everything fits on one page.
    if (this.controls) {
      this.controls.style.display = this.pageCount <= 1 ? 'none' : '';
    }
    if (this.sideArrows) {
      this.sideArrows.hidden = this.pageCount <= 1;
    }
    this.syncControls();
  }

  syncControls() {
    this.dots?.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.page;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (this.counter) {
      this.counter.textContent = `${this.page + 1} / ${this.pageCount}`;
      this.counter.setAttribute('aria-label', `Page ${this.page + 1} of ${this.pageCount}`);
    }

    const atStart = this.page <= 0 && this.track.scrollLeft <= 2;
    const atEnd = this.page >= this.pageCount - 1 || this.isAtScrollEnd();
    this.prevButtons.forEach((button) => {
      button.disabled = atStart;
    });
    this.nextButtons.forEach((button) => {
      button.disabled = atEnd;
    });
  }
}

if (!customElements.get('core7-carousel')) {
  customElements.define('core7-carousel', Core7Carousel);
}
